import React, { useState, useEffect } from 'react'
import { Box, Typography, Chip, IconButton, Button, CircularProgress, Select, MenuItem, FormControl } from '@mui/material'
import {
  Heart,
  MessageCircle,
  Share2,
  Eye,
  ExternalLink,
  Sparkles,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Send,
  X,
} from 'lucide-react'
import {
  Platform,
  useGetActiveWeeklyContextQuery,
  useGetBulkGenerateSummaryVibeQuery,
  useSaveContentCommentMutation,
  useHideTopicContentMutation,
} from 'graphql/generated/graphql'
import { useNalapodExtension } from 'hooks/useNalapodExtension'
import { LOCAL, DEVELOP } from 'constants/env'
import { COMMENT_STYLES, CommentStyle } from 'constants/commentStyles'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

interface WeeklyContext {
  books?: string[]
  activities?: string[]
  media?: string[]
  topics?: string[]
  freeform?: string
}

interface UserVibe {
  description?: string
  examples?: string[]
}

interface NicheContent {
  id: string
  platform: Platform
  contentId: string
  caption?: string | null
  summary?: string | null
  authorUsername: string
  authorDisplayName?: string | null
  authorAvatarUrl?: string | null
  contentUrl: string
  thumbnailUrl?: string | null
  contentType: string
  likes: number
  comments: number
  shares: number
  views?: number | null
  hashtags: string[]
  postedAt: string
  similarity?: number | null
  interactedWith?: boolean | null
  preGeneratedComment?: string | null
}

interface ContentFeedProps {
  content: NicheContent[]
  onInteract?: (contentId: string) => void
  onHide?: (contentId: string) => void
}

const platformColors: Record<Platform, string> = {
  [Platform.Instagram]: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
  [Platform.TikTok]: 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)',
  [Platform.Youtube]: '#ff0000',
  [Platform.Reddit]: '#ff4500',
  [Platform.Facebook]: '#1877f2',
  [Platform.Twitter]: '#1da1f2',
  [Platform.Bluesky]: '#0085ff',
}

const platformIcons: Record<Platform, string> = {
  [Platform.Instagram]: '📷',
  [Platform.TikTok]: '🎵',
  [Platform.Youtube]: '🎥',
  [Platform.Reddit]: '🔴',
  [Platform.Facebook]: '📘',
  [Platform.Twitter]: '🐦',
  [Platform.Bluesky]: '🦋',
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return date.toLocaleDateString()
}

interface ContentCardProps {
  item: NicheContent
  userVibe?: UserVibe
  weeklyContext?: WeeklyContext
  onInteract?: (contentId: string) => void
  onHide?: (contentId: string) => void
}

function ContentCard({ item, userVibe, weeklyContext, onInteract, onHide }: ContentCardProps) {
  const [generatingComment, setGeneratingComment] = useState(false)
  const [generatedComment, setGeneratedComment] = useState<string | null>(item.preGeneratedComment ?? null)
  const [copied, setCopied] = useState(false)
  const [summaryExpanded, setSummaryExpanded] = useState(false)
  const [replying, setReplying] = useState(false)
  const { openReply } = useNalapodExtension()
  const [saveComment] = useSaveContentCommentMutation()
  const [selectedStyle, setSelectedStyle] = useState<CommentStyle>(() => {
    try {
      const stored = localStorage.getItem('discoverCommentStyle')
      if (stored && Object.keys(COMMENT_STYLES).includes(stored)) {
        return stored as CommentStyle
      }
      return 'casual'
    } catch {
      return 'casual'
    }
  })

  // Save selected style to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('discoverCommentStyle', selectedStyle)
    } catch (error) {
      console.error('Error saving comment style:', error)
    }
  }, [selectedStyle])

  const handleGenerateComment = async () => {
    setGeneratingComment(true)
    try {
      const response = await fetch(`${config.API_URL}/api/generate-comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          hashtags: item.hashtags ?? [],
          summary: item.summary ?? '',
          caption: item.caption ?? '',
          selectedStyle: selectedStyle,
          userVibe: userVibe ?? undefined,
          weeklyContext: weeklyContext ?? undefined,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to generate comment')
      }

      const data = await response.json()

      if (data.success && data.comment) {
        const newComment = data.comment.trim()
        setGeneratedComment(newComment)
        // Persist to DB so it survives navigation
        saveComment({ variables: { contentId: item.id, comment: newComment } }).catch(console.error)
      }
    } catch (error) {
      console.error('Error generating comment:', error)
    } finally {
      setGeneratingComment(false)
    }
  }

  const handleCopyComment = () => {
    if (generatedComment) {
      void navigator.clipboard.writeText(generatedComment)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      onInteract?.(item.id)
    }
  }

  const handleReply = async () => {
    if (!generatedComment) return

    // Always copy to clipboard as a fallback
    void navigator.clipboard.writeText(generatedComment)
    setReplying(true)

    const isSupported = item.contentUrl.includes('instagram.com') || item.contentUrl.includes('tiktok.com')

    if (isSupported) {
      // Try the extension first — it will open the tab and prefill the comment
      const ok = await openReply(item.contentUrl, generatedComment)
      if (!ok) {
        // Fallback: just open the URL
        window.open(item.contentUrl, '_blank')
      }
    } else {
      window.open(item.contentUrl, '_blank')
    }

    setReplying(false)
    onInteract?.(item.id)
  }

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        breakInside: 'avoid',
        marginBottom: '16px',
        opacity: item.interactedWith ? 0.45 : 1,
        '&:hover': {
          opacity: item.interactedWith ? 0.7 : 1,
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderColor: 'rgba(255,255,255,0.15)',
        },
      }}
    >
      {/* Header with Platform and Similarity */}
      <Box
        sx={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Platform Badge */}
        <Box
          sx={{
            padding: '4px 10px',
            borderRadius: '8px',
            background: platformColors[item.platform],
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span style={{ fontSize: '12px' }}>{platformIcons[item.platform]}</span>
          <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 600 }}>
            {item.platform}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Similarity Badge */}
          {item.similarity && item.similarity > 0 && (
            <Box
              sx={{
                padding: '4px 8px',
                borderRadius: '6px',
                backgroundColor: 'rgba(168, 85, 247, 0.15)',
              }}
            >
              <Typography sx={{ color: '#a855f7', fontSize: '11px', fontWeight: 600 }}>
                {Math.round(item.similarity * 100)}% match
              </Typography>
            </Box>
          )}
          {/* Hide / dismiss button */}
          {onHide && (
            <IconButton
              onClick={(e) => { e.stopPropagation(); onHide(item.id) }}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.2)',
                padding: '2px',
                '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.1)' },
              }}
            >
              <X size={14} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Summary Section */}
      <Box
        sx={{
          padding: '16px',
          backgroundColor: 'rgba(168, 85, 247, 0.03)',
          borderBottom: '1px solid rgba(168, 85, 247, 0.1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}
        >
          <Typography
            sx={{
              color: '#a855f7',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
            }}
          >
            Summary of Content
          </Typography>
          {item.summary && (
            <IconButton
              onClick={() => setSummaryExpanded(!summaryExpanded)}
              sx={{
                color: '#a855f7',
                padding: '2px',
                '&:hover': { backgroundColor: 'rgba(168, 85, 247, 0.1)' },
              }}
            >
              {summaryExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </IconButton>
          )}
        </Box>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: '13px',
            lineHeight: 1.6,
            ...(!summaryExpanded && item.summary
              ? {
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }
              : {}),
          }}
        >
          {item.summary ?? 'No summary available yet. Summary will be generated shortly.'}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ padding: '16px' }}>
        {/* Author */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          {item.authorAvatarUrl ? (
            <Box
              component="img"
              src={item.authorAvatarUrl}
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: 'rgba(168, 85, 247, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '14px' }}>
                {item.authorUsername.charAt(0).toUpperCase()}
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>
              @{item.authorUsername}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
              {formatDate(item.postedAt)}
            </Typography>
          </Box>
          <IconButton
            onClick={() => window.open(item.contentUrl, '_blank')}
            sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}
          >
            <ExternalLink size={16} />
          </IconButton>
        </Box>

        {/* Caption */}
        {item.caption && (
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '13px',
              lineHeight: 1.5,
              marginBottom: '12px',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.caption}
          </Typography>
        )}

        {/* Hashtags */}
        {item.hashtags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
            {item.hashtags.slice(0, 5).map((tag, i) => (
              <Chip
                key={i}
                label={`#${tag.replace(/^#/, '')}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(168, 85, 247, 0.1)',
                  color: '#a855f7',
                  fontSize: '11px',
                  height: '24px',
                }}
              />
            ))}
            {item.hashtags.length > 5 && (
              <Chip
                label={`+${item.hashtags.length - 5}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '11px',
                  height: '24px',
                }}
              />
            )}
          </Box>
        )}

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Heart size={14} color="rgba(255,255,255,0.5)" />
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
              {formatNumber(item.likes)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MessageCircle size={14} color="rgba(255,255,255,0.5)" />
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
              {formatNumber(item.comments)}
            </Typography>
          </Box>
          {item.shares > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Share2 size={14} color="rgba(255,255,255,0.5)" />
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                {formatNumber(item.shares)}
              </Typography>
            </Box>
          )}
          {item.views && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Eye size={14} color="rgba(255,255,255,0.5)" />
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                {formatNumber(item.views)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Comment Style Selector */}
        <Box sx={{ marginBottom: '12px' }}>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              marginBottom: '6px',
            }}
          >
            Comment Style
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedStyle}
              onChange={(e) => setSelectedStyle(e.target.value as CommentStyle)}
              sx={{
                backgroundColor: 'rgba(168, 85, 247, 0.05)',
                color: 'rgba(255,255,255,0.9)',
                fontSize: '12px',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(168, 85, 247, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(168, 85, 247, 0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#a855f7',
                },
                '& .MuiSelect-icon': {
                  color: '#a855f7',
                },
              }}
            >
              {Object.entries(COMMENT_STYLES).map(([key, style]) => (
                <MenuItem key={key} value={key} sx={{ fontSize: '12px' }}>
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                      {style.label}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
                      {style.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Generate Comment Button & Generated Comment */}
        <Box>
          <Button
            onClick={() => {
              handleGenerateComment().catch(console.error)
            }}
            disabled={generatingComment}
            fullWidth
            sx={{
              backgroundColor: 'rgba(168, 85, 247, 0.1)',
              color: '#a855f7',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(168, 85, 247, 0.2)',
              },
              '&:disabled': {
                color: 'rgba(168, 85, 247, 0.5)',
              },
            }}
            startIcon={generatingComment ? <CircularProgress size={14} sx={{ color: '#a855f7' }} /> : <Sparkles size={14} />}
          >
            {generatingComment ? 'Generating...' : 'Generate AI Comment'}
          </Button>

          {/* Generated Comment */}
          {generatedComment && (
            <Box
              sx={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: 'rgba(168, 85, 247, 0.05)',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                borderRadius: '8px',
              }}
            >
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', lineHeight: 1.5, marginBottom: '12px' }}>
                {generatedComment}
              </Typography>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <Button
                  onClick={handleCopyComment}
                  size="small"
                  sx={{
                    flex: 1,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: copied ? '#22c55e' : 'rgba(255,255,255,0.7)',
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '11px',
                    padding: '6px 12px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                  startIcon={copied ? <Check size={12} /> : <Copy size={12} />}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  onClick={() => { handleReply().catch(console.error) }}
                  size="small"
                  sx={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '6px 12px',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                    },
                  }}
                  startIcon={replying ? <CircularProgress size={12} sx={{ color: 'white' }} /> : <Send size={12} />}
                >
                  {replying ? 'Opening...' : 'Reply'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default function ContentFeed({ content, onInteract, onHide }: ContentFeedProps) {
  // Fetch user vibe (without campaignId for global/default vibe)
  const { data: vibeData } = useGetBulkGenerateSummaryVibeQuery()

  // Fetch weekly context for AI comment generation
  const { data: weeklyContextData } = useGetActiveWeeklyContextQuery()

  // Safely parse user vibe (which may be a JSON string or an object)
  let userVibe: UserVibe = {}
  try {
    const rawVibe = vibeData?.getBulkGenerateSummaryVibe
    userVibe = typeof rawVibe === 'string' ? JSON.parse(rawVibe) : rawVibe ?? {}
  } catch (err) {
    console.error('Error parsing userVibe data:', err)
    userVibe = {}
  }

  // Get weekly context for AI generation
  const weeklyContext = weeklyContextData?.getActiveWeeklyContext?.context as WeeklyContext | undefined

  return (
    <Box
      sx={{
        columnCount: { xs: 1, sm: 2, md: 3 },
        columnGap: '16px',
      }}
    >
      {content.map((item) => (
        <ContentCard
          key={item.id}
          item={item}
          userVibe={userVibe}
          weeklyContext={weeklyContext}
          onInteract={onInteract}
          onHide={onHide}
        />
      ))}
    </Box>
  )
}
